import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";

import ConfiguracionCard from "./ConfiguracionCard";
import Loader from "../../components/UI/Loader";
import Alert from "../../components/UI/Alert";

import {
  getConfiguracionesThunk,
  completarSesionThunk,
} from "../../store/conversacion/conversacion.thunks";

const ConversacionActiva = ({ sesion, onCompletada, onVerResumen }) => {
  const dispatch = useDispatch();
  const {
    configuraciones,
    respuestas,
    configuracionesLoading,
    respondiendo,
    completando,
    error,
  } = useSelector((state) => state.conversacion);
  const { user } = useSelector((state) => state.auth);

  const [showCompletarConfirm, setShowCompletarConfirm] = useState(false);

  const isMentor = user?.rol?.trim() === "MENTOR" || user?.is_superuser;
  const isEstudiante = user?.rol?.trim() === "ESTUDIANTE" && !user?.is_superuser;
  const miTipoAutor = isMentor ? "MENTOR" : "ESTUDIANTE";

  useEffect(() => {
    if (sesion?.id) {
      dispatch(getConfiguracionesThunk(sesion.id));
    }
  }, [sesion?.id, dispatch]);

  const handleCompletar = useCallback(async () => {
    const result = await dispatch(completarSesionThunk({ sesion_id: sesion.id }));
    if (result.type.endsWith("/fulfilled")) {
      toast.success("¡Sesión completada exitosamente!", { position: "top-center" });
      onCompletada();
    } else {
      toast.error(result.payload || "Error al completar la sesión", { position: "top-center" });
    }
    setShowCompletarConfirm(false);
  }, [sesion?.id, dispatch, onCompletada]);

  if (configuracionesLoading) {
    return <Loader center text="Cargando configuraciones..." delay={200} />;
  }

  return (
    <div className="conversacion-activa">
      {/* Cabecera de sesión */}
      <div className="conversacion-card card mb-4">
        <div className="card-body p-4">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div className="d-flex align-items-center gap-3">
              <div className="conversacion-icon-wrapper bg-primary-subtle" style={{ width: 48, height: 48, borderRadius: 12 }}>
                <Icon icon="mdi:forum" width="24" className="text-primary" />
              </div>
              <div>
                <h5 className="mb-1">
                  Conversación #{sesion.conversacion_numero}
                </h5>
                <p className="text-secondary mb-0" style={{ fontSize: "0.875rem" }}>
                  <span className="fw-medium text-body">{sesion.conversacion_pregunta}</span>
                  {" · "}
                  <Icon icon="mdi:star" width="14" className="me-1" />
                  {sesion.talento_nombre}
                  {" · "}
                  {sesion.mentor_nombre} → {sesion.estudiante_nombre}
                </p>
              </div>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => onVerResumen(sesion.id)}
              >
                <Icon icon="mdi:eye-outline" className="me-1" width="16" /> Ver resumen
              </button>
              {isMentor && (
                <button
                  className="btn btn-success btn-sm"
                  onClick={() => setShowCompletarConfirm(true)}
                  disabled={completando || respondiendo}
                >
                  <Icon icon="mdi:check-circle-outline" className="me-1" width="16" />
                  {completando ? "Completando..." : "Completar sesión"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <Alert
          message={error}
          variant="error"
          onClose={() => dispatch({ type: "conversacion/clearError" })}
          className="mb-3"
        />
      )}

      <div className="row g-4">
        {configuraciones.map((config) => (
          <div key={config.id} className="col-12">
            <ConfiguracionCard
              config={config}
              respuestas={respuestas}
              sesionId={sesion.id}
              miTipoAutor={miTipoAutor}
              isMentor={isMentor}
              isEstudiante={isEstudiante}
            />
          </div>
        ))}
      </div>

      {configuraciones.length === 0 && !configuracionesLoading && (
        <div className="card p-4 radius-12 text-center">
          <Icon icon="mdi:alert-circle-outline" width="40" className="text-muted mb-2" />
          <p className="text-secondary mb-0">No hay configuraciones disponibles para esta sesión.</p>
        </div>
      )}

      {showCompletarConfirm && (
        <div className="modal-overlay">
          <div className="modal-content card p-4 radius-12">
            <h5 className="mb-3">¿Completar la sesión?</h5>
            <p className="text-secondary mb-4">
              Una vez completada, no se podrán agregar ni modificar respuestas.
            </p>
            <div className="d-flex gap-2 justify-content-end">
              <button className="btn btn-outline-secondary" onClick={() => setShowCompletarConfirm(false)}>
                Cancelar
              </button>
              <button className="btn btn-success" onClick={handleCompletar} disabled={completando}>
                {completando ? "Completando..." : "Sí, completar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversacionActiva;
